import * as React from "react";
import { getLabelForOrderMethodStatus } from "../../../../../helpers";
import { makeStyles } from "@mui/styles";
import {
  markOrderAsEmbroideryCompletedMutation,
  markOrderAsEmbroideryStartedMutation,
  markOrderAsNoLongerOnHoldMutation,
  markOrderAsTrimmingCompletedMutation
} from "../../../../../graph";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import JobStatusIcon from "../../../../../components/JobStatusIcon";
import PlaceOrderOnHoldModal from "../../../../../components/PlaceOrderOnHoldModal";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../../../components/withSnackbar";

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(1)
  },

  jobStatus: {
    marginLeft: theme.spacing(1)
  },

  spaceBetweenContainer: {
    display: "flex"
  },

  spacerRight: {
    marginRight: theme.spacing(1)
  },

  spinnerWrapper: {
    padding: theme.spacing(1),
    position: "relative",
    textAlign: "center"
  }
}));

const EmbroideryOrderStatus = ({
  order: {
    canViewerCompleteEmbroidery,
    canViewerCompleteTrimming,
    canViewerPlaceOnHold,
    canViewerRemoveHold,
    canViewerStartEmbroidery,
    embroideryStatus,
    id: orderId
  },
  showErrorSnackbar,
  showSuccessSnackbar
}) => {
  const classes = useStyles();

  // States:
  const [isMutatingOrder, setIsMutatingOrder] = useState(false);
  const [isPlacingOrderOnHold, setIsPlacingOrderOnHold] = useState(false);

  // Mutations:
  const [markOrderAsEmbroideryCompleted] = useMutation(
    markOrderAsEmbroideryCompletedMutation
  );
  const [markOrderAsEmbroideryStarted] = useMutation(
    markOrderAsEmbroideryStartedMutation
  );
  const [markOrderAsNoLongerOnHold] = useMutation(
    markOrderAsNoLongerOnHoldMutation
  );
  const [markOrderAsTrimmingCompleted] = useMutation(
    markOrderAsTrimmingCompletedMutation
  );

  // Handlers:
  const handleHoldButtonClick = () => setIsPlacingOrderOnHold(true);

  const handlePlaceOrderOnHoldModalRequestClose = () =>
    setIsPlacingOrderOnHold(false);

  const handleEmbroideryCompletedButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsEmbroideryCompleted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsEmbroideryCompleted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleRemoveHoldButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsNoLongerOnHold({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsNoLongerOnHold: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleStartButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsEmbroideryStarted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsEmbroideryStarted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleTrimmingCompleteButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsTrimmingCompleted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsTrimmingCompleted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  return (
    <div>
      <div className={classes.spaceBetweenContainer}>
        <JobStatusIcon status={embroideryStatus} />
        <div className={classes.jobStatus}>
          <Typography variant="h6">Embroidery</Typography>
          <Typography variant="body2">
            {getLabelForOrderMethodStatus(embroideryStatus)}
          </Typography>
        </div>
      </div>
      {isMutatingOrder ? (
        <div className={classes.spinnerWrapper}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.buttonContainer}>
          {canViewerStartEmbroidery && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleStartButtonClick}
            >
              Start
            </Button>
          )}
          {canViewerCompleteEmbroidery && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleEmbroideryCompletedButtonClick}
            >
              Embroidery Complete
            </Button>
          )}
          {canViewerCompleteTrimming && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleTrimmingCompleteButtonClick}
            >
              Trimming Complete
            </Button>
          )}
          {canViewerPlaceOnHold && (
            <Button
              color="secondary"
              onClick={handleHoldButtonClick}
              variant="contained"
            >
              Hold
            </Button>
          )}
          {canViewerRemoveHold && (
            <Button
              color="secondary"
              onClick={handleRemoveHoldButtonClick}
              variant="contained"
            >
              Remove Hold
            </Button>
          )}
        </div>
      )}
      <PlaceOrderOnHoldModal
        isOpen={isPlacingOrderOnHold}
        onRequestClose={handlePlaceOrderOnHoldModalRequestClose}
        orderId={orderId}
      />
    </div>
  );
};

export default withSnackbar(EmbroideryOrderStatus);
